import { zh } from './locales/zh';
import { en } from './locales/en';
import { de } from './locales/de';
import { ja } from './locales/ja';
import { ru } from './locales/ru';
import { fr } from './locales/fr';
import { es } from './locales/es';
import { id } from './locales/id';
import { ar } from './locales/ar';
import { bo } from './locales/bo';
import type { Translations } from './locales/zh';
import type { SupportedLocale } from '../types';

const dictionaries: Record<SupportedLocale, Translations> = {
  zh,
  en,
  de,
  ja,
  ru,
  fr,
  es,
  id,
  ar,
  bo,
};

export type LocaleDir = 'ltr' | 'rtl';

export interface LocaleMeta {
  value: SupportedLocale;
  /** native-name label, shown in the language selector */
  label: string;
  dir: LocaleDir;
}

/**
 * Task 13: 9-locale support. Order here is the order shown in the language selector.
 * `dir` drives root `dir=` attribute and CSS logical mirroring (Arabic = rtl).
 */
export const SUPPORTED_LOCALES: readonly LocaleMeta[] = [
  { value: 'zh', label: '中文', dir: 'ltr' },
  { value: 'en', label: 'English', dir: 'ltr' },
  { value: 'de', label: 'Deutsch', dir: 'ltr' },
  { value: 'ja', label: '日本語', dir: 'ltr' },
  { value: 'ru', label: 'Русский', dir: 'ltr' },
  { value: 'fr', label: 'Français', dir: 'ltr' },
  { value: 'es', label: 'Español', dir: 'ltr' },
  { value: 'id', label: 'Bahasa Indonesia', dir: 'ltr' },
  { value: 'ar', label: 'العربية', dir: 'rtl' },
  { value: 'bo', label: 'བོད་སྐད་', dir: 'ltr' },
] as const;

export const SUPPORTED_LOCALE_VALUES: readonly SupportedLocale[] =
  SUPPORTED_LOCALES.map((l) => l.value);

const STORAGE_KEY = 'dcs.widget.locale';

function isSupported(v: string | null | undefined): v is SupportedLocale {
  return !!v && (SUPPORTED_LOCALE_VALUES as readonly string[]).includes(v);
}

/** 解析浏览器语言 (例如 'zh-CN' -> 'zh', 'en-US' -> 'en') */
function matchByPrefix(lang: string): SupportedLocale | null {
  const lower = lang.toLowerCase();
  // 完整 BCP47 优先 (如 'pt-BR' 不会命中,但 'zh-Hant' 仍归类到 'zh')
  for (const meta of SUPPORTED_LOCALES) {
    if (lower === meta.value || lower.startsWith(`${meta.value}-`)) {
      return meta.value;
    }
  }
  return null;
}

function readUrlLang(): SupportedLocale | null {
  if (typeof window === 'undefined') return null;
  try {
    const params = new URLSearchParams(window.location.search);
    const v = params.get('lang');
    return isSupported(v) ? v : null;
  } catch {
    return null;
  }
}

function readStoredLocale(): SupportedLocale | null {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    return isSupported(v) ? v : null;
  } catch {
    /* SSR / privacy mode */
    return null;
  }
}

/**
 * 读取宿主页面声明的当前内容语言。
 * 中途岛3 站点在唯一根容器 div 上输出 `data-lang="<ISO 639-1 短码>"`（如 zh/en/de），
 * 值跟随当前页真实语言；`<html lang>` 恒为 zh-CN 不可用。全页仅此一处带 data-lang
 * （语言切换器不带），故 querySelector 命中唯一。
 * 文档支持但本 widget 未支持的码（如 ko 韩语）返回 null，交后续策略兜底。
 */
function readHostPageLang(): SupportedLocale | null {
  if (typeof document === 'undefined') return null;
  try {
    const raw = document.querySelector('[data-lang]')?.getAttribute('data-lang');
    if (!raw) return null;
    const v = raw.trim().toLowerCase();
    if (isSupported(v)) return v;
    // 容错:万一宿主输出带 region(如 zh-CN),按前缀归一;ko 等不支持码 → null
    return matchByPrefix(v);
  } catch {
    return null;
  }
}

function readMerchantDefault(): SupportedLocale | null {
  // config.value?.defaultLocale 字段尚未在 shared schema 中落地 (spec 第 2 期)。
  // 此处保留 hook,后续 schema 加 defaultLocale 时无需改 detectLocale。
  // 注意:为避免与 widget-store 的循环依赖,这里不直接 import,留空。
  return null;
}

function readNavigatorLang(): SupportedLocale | null {
  if (typeof navigator === 'undefined') return null;
  const candidates: string[] = [];
  if (Array.isArray(navigator.languages)) candidates.push(...navigator.languages);
  if (navigator.language) candidates.push(navigator.language);
  for (const c of candidates) {
    const hit = matchByPrefix(c);
    if (hit) return hit;
  }
  return null;
}

/**
 * 自动检测优先级 (高 → 低):
 *   1. URL 参数 ?lang=xx
 *   2. localStorage('dcs.widget.locale') — 用户上次手动选择
 *   3. 宿主页 <div data-lang="xx"> — 当前页真实内容语言 (新访客自动跟随页面)
 *   4. 商户配置 (config.defaultLocale,字段未落地时为 null,占位 hook)
 *   5. navigator.language(s) 前缀匹配
 *   6. fallback (默认 'zh')
 */
export function detectLocale(fallback: SupportedLocale = 'zh'): SupportedLocale {
  return (
    readUrlLang() ??
    readStoredLocale() ??
    readHostPageLang() ??
    readMerchantDefault() ??
    readNavigatorLang() ??
    fallback
  );
}

export function setLocalePreference(locale: SupportedLocale): void {
  try {
    localStorage.setItem(STORAGE_KEY, locale);
  } catch {
    /* ignore */
  }
}

export function getDirForLocale(locale: SupportedLocale): LocaleDir {
  return SUPPORTED_LOCALES.find((m) => m.value === locale)?.dir ?? 'ltr';
}

export interface I18n {
  locale: SupportedLocale;
  dir: LocaleDir;
  t: (key: string) => string;
}

/**
 * 极简 t():路径如 'panel.welcomeFallback'。
 * 命中失败时返回 key 自身,便于开发期肉眼发现漏译。
 */
export function createI18n(locale: SupportedLocale): I18n {
  const dict = dictionaries[locale] ?? dictionaries.zh;
  const t = (path: string): string => {
    const parts = path.split('.');
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    let cur: any = dict;
    for (const p of parts) {
      if (cur && typeof cur === 'object' && p in cur) {
        cur = cur[p];
      } else {
        return path;
      }
    }
    return typeof cur === 'string' ? cur : path;
  };
  return { locale, dir: getDirForLocale(locale), t };
}
